import CellTowerIcon from '@mui/icons-material/CellTower';
import ClassIcon from '@mui/icons-material/Class';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import { alpha, Theme } from '@mui/material';
import { $T } from '../../../locale/types';
import {
  stationFilterOptionTypeLongMessages,
  stationFilterOptionTypeShortMessages,
  stationTypeMessages,
} from '../station-messages';
import { OptionType, StationFilterOption } from './types';
import { StationType } from '@app/core/types/stations';

/** Maps OptionType to the key used in station filter option type messages. */
const optionTypeToMessageKey: Record<StationFilterOption['type'], string> = {
  [OptionType.StationType]: 'stationType',
  [OptionType.StationGroup]: 'stationGroup',
  [OptionType.IndividualStation]: 'individualStation',
};

/** Returns the localized label for a station type (e.g. Independiente, Cabecera). */
export const getStationTypeLabel = ($t: $T, type: StationType): string =>
  $t(stationTypeMessages[type]);

// Helper function to generate ID from type and value
export const generateOptionId = (option: StationFilterOption): string => {
  switch (option.type) {
    case OptionType.StationType:
      return `${option.type}-${option.value}`;
    case OptionType.StationGroup:
      return `${option.type}-${option.value.id}`;
    case OptionType.IndividualStation:
      return `${option.type}-${option.value.id}`;
    default:
      return '';
  }
};

// Helper function to get icon for option type
export const getOptionIcon = (type: StationFilterOption['type']) => {
  switch (type) {
    case OptionType.StationType:
      return <ClassIcon fontSize="inherit" />;
    case OptionType.StationGroup:
      return <WorkspacesIcon fontSize="inherit" />;
    case OptionType.IndividualStation:
      return <CellTowerIcon fontSize="inherit" />;
    default:
      return null;
  }
};

/** Light-mode background tint mapped to semantic palette roles (info / warning / success). */
function getLightOptionBackground(type: StationFilterOption['type'], theme: Theme): string {
  switch (type) {
    case OptionType.StationType:
      return theme.palette.info.light;
    case OptionType.StationGroup:
      return theme.palette.warning.light;
    case OptionType.IndividualStation:
      return theme.palette.success.light;
    default:
      return theme.palette.grey[100];
  }
}

/** Dark-mode semi-transparent tint so chips and group headers stay distinguishable. */
function getDarkOptionBackground(type: StationFilterOption['type'], theme: Theme): string {
  switch (type) {
    case OptionType.StationType:
      return alpha(theme.palette.info.main, 0.12);
    case OptionType.StationGroup:
      return alpha(theme.palette.warning.main, 0.12);
    case OptionType.IndividualStation:
      return alpha(theme.palette.success.main, 0.12);
    default:
      return theme.palette.action.hover;
  }
}

/**
 * Returns the background color for group headers for the given option type.
 * Uses semantic palette tokens (info / warning / success) per category.
 */
export const getOptionColor = (type: StationFilterOption['type'], theme: Theme): string => {
  return theme.palette.mode === 'dark'
    ? getDarkOptionBackground(type, theme)
    : getLightOptionBackground(type, theme);
};

/** Returns the localized group header for the given option type and variant. */
export const getGroupHeader = (
  $t: $T,
  type: StationFilterOption['type'],
  variant: 'short' | 'long',
): string => {
  const key = optionTypeToMessageKey[type];
  if (!key) return '';
  const messages =
    variant === 'long' ? stationFilterOptionTypeLongMessages : stationFilterOptionTypeShortMessages;
  return $t(messages[key]);
};

/** Light-mode chip tint — softer than palette.*.light, stronger than a bare wash. */
const LIGHT_CHIP_TINT_ALPHA = 0.1;

/** Light-mode chip background. */
function getLightChipBackground(type: StationFilterOption['type'], theme: Theme): string {
  switch (type) {
    case OptionType.StationType:
      return alpha(theme.palette.info.main, LIGHT_CHIP_TINT_ALPHA);
    case OptionType.StationGroup:
      return alpha(theme.palette.warning.main, LIGHT_CHIP_TINT_ALPHA);
    case OptionType.IndividualStation:
      return alpha(theme.palette.success.main, LIGHT_CHIP_TINT_ALPHA);
    default:
      return theme.palette.grey[100];
  }
}

function getChipBackgroundColor(type: StationFilterOption['type'], theme: Theme): string {
  if (theme.palette.mode === 'dark') {
    // Keep previous dark chip colors (same as group headers).
    return getDarkOptionBackground(type, theme);
  }

  return getLightChipBackground(type, theme);
}

function getChipBorderColor(type: StationFilterOption['type'], theme: Theme): string {
  if (theme.palette.mode === 'dark') {
    return alpha(theme.palette.text.primary, 0.24);
  }

  switch (type) {
    case OptionType.StationType:
      return alpha(theme.palette.info.main, 0.22);
    case OptionType.StationGroup:
      return alpha(theme.palette.warning.main, 0.22);
    case OptionType.IndividualStation:
      return alpha(theme.palette.success.main, 0.22);
    default:
      return theme.palette.grey[300];
  }
}

/** Returns chip background and border colors for the given option type. */
export const getChipStyles = (type: StationFilterOption['type'], theme: Theme) => ({
  backgroundColor: getChipBackgroundColor(type, theme),
  borderColor: getChipBorderColor(type, theme),
});

// Helper function to sort options by type priority
export const sortOptionsByType = (options: StationFilterOption[]): StationFilterOption[] => {
  const typeOrder = [OptionType.StationType, OptionType.StationGroup, OptionType.IndividualStation];

  return [...options].sort((a, b) => {
    // First sort by type priority
    const aTypeIndex = typeOrder.indexOf(a.type);
    const bTypeIndex = typeOrder.indexOf(b.type);

    if (aTypeIndex !== bTypeIndex) {
      return aTypeIndex - bTypeIndex;
    }

    // Then sort alphabetically within each type
    return a.label.localeCompare(b.label);
  });
};
